import { WEB_PAGES } from '@praxadon/shared/pages';
import { Button, Field, Input, Spinner } from '@praxadon/ui';
import { type SubmitEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { ApiError, describeError } from '../../api/client.ts';
import { onboarding } from '../../api/endpoints.ts';
import { useSession } from '../../app/session.tsx';
import { AuthLayout, FormError } from './AuthLayout.tsx';
import { PasswordInput, passwordHint } from './PasswordInput.tsx';
import { UsernameField } from './UsernameField.tsx';

/** Accepting a personal email invitation: the link carries a single-use token. */
export function InvitePage() {
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [invite, setInvite] = useState<{ email: string; tenantName: string; role: string } | null>(
    null,
  );
  const [fetchError, setFetchError] = useState<string | null>(null);
  const loadError = token
    ? fetchError
    : 'This invitation link is incomplete. Please use the link from your email.';
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    onboarding.invite(token).then(
      (result) => {
        setInvite(result);
        if (result.suggestedUsername) setUsername(result.suggestedUsername);
      },
      (failure: unknown) => {
        setFetchError(describeError(failure));
      },
    );
  }, [token]);

  if (loadError) {
    return (
      <AuthLayout
        form={false}
        file="join.html"
        title="This invitation can't be used"
        subtitle={loadError}
        footer={
          <Link className="underline hover:text-fg" to={WEB_PAGES.login}>
            Go to sign in
          </Link>
        }
      >
        <p className="text-center text-sm text-fg-muted">
          Ask your instructor to send you a new invitation.
        </p>
      </AuthLayout>
    );
  }
  if (!invite) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner size={24} label="Loading your invitation" />
      </div>
    );
  }

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await onboarding.acceptInvite({ token, username, displayName, password });
      await refresh();
      // An invited instructor still has two-step setup to do; the sign-in page resumes it.
      void navigate(WEB_PAGES.login, { replace: true });
    } catch (failure) {
      if (failure instanceof ApiError && Object.keys(failure.fields).length > 0)
        setFields(failure.fields);
      else setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      file="join.html"
      title={`Join ${invite.tenantName}`}
      subtitle={
        <>
          You were invited as <span className="font-medium text-fg">{invite.email}</span>
          {invite.role === 'tenant_admin' ? ', as an instructor' : ''}.
        </>
      }
    >
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4" noValidate>
        <FormError message={error} />
        <Field label="Your name" error={fields['displayName']}>
          <Input
            autoComplete="name"
            maxLength={80}
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.target.value);
            }}
          />
        </Field>
        <UsernameField value={username} onChange={setUsername} serverError={fields['username']} />
        <Field label="Password" hint={passwordHint(password)} error={fields['password']}>
          <PasswordInput
            autoComplete="new-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </Field>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={busy}
          disabled={!displayName || !username || !password}
        >
          Accept invitation
        </Button>
      </form>
    </AuthLayout>
  );
}
